import React, { useState, useEffect } from 'react';
import api, { uploadMedia } from '../utils/api';
import ImageCropperModal from './ImageCropperModal';
import './AddFacultyStaffDialog.css';

const AddFacultyStaffDialog = ({ isOpen, onClose, onSave, faculty }) => {
  const DESIGNATION_OPTIONS = [
    { value: 'ডিন', label: 'ডিন' },
    { value: 'রেজিস্ট্রার', label: 'রেজিস্ট্রার' },
    { value: 'ডেপুটি রেজিস্ট্রার', label: 'ডেপুটি রেজিস্ট্রার' },
    { value: 'সেকশন অফিসার', label: 'সেকশন অফিসার' },
    { value: 'এমএলএসএস', label: 'এমএলএসএস' },
    { value: 'ল্যাব অ্যাটেন্ডেন্ট', label: 'ল্যাব অ্যাটেন্ডেন্ট' },
    { value: '__custom__', label: 'কাস্টম (নিজে লিখুন)' },
  ];

  const [formData, setFormData] = useState({
    name: '',
    nameEn: '',
    email: '',
    phoneNumber: '',
    designation: '',
    priority: 1
  });
  const [designationOption, setDesignationOption] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState('');
  const [teachers, setTeachers] = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [showTeachersList, setShowTeachersList] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch all teachers from this faculty's departments
  useEffect(() => {
    const fetchTeachers = async () => {
      if (!faculty || !isOpen) return;
      
      setLoadingTeachers(true);
      try {
        // Fetch all departments in this faculty
        const deptsResponse = await api.get(`/departments?faculty_ref=${faculty._id}`);
        if (deptsResponse.data.success) {
          const departments = deptsResponse.data.data;
          
          // Fetch teachers from all departments
          const teacherPromises = departments.map(dept => 
            api.get(`/teachers?dept_ref=${dept._id}`)
          );
          
          const teacherResponses = await Promise.all(teacherPromises);
          const allTeachers = teacherResponses.flatMap(response => 
            response.data.success ? response.data.data : []
          );
          
          setTeachers(allTeachers);
        }
      } catch (err) {
        console.error('Error fetching teachers:', err);
      } finally {
        setLoadingTeachers(false);
      }
    };

    fetchTeachers();
  }, [faculty, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectTeacher = (teacher) => {
    setFormData({
      name: teacher.name || '',
      nameEn: teacher.nameEn || '',
      email: teacher.email || '',
      phoneNumber: teacher.phoneNumber || '',
      designation: '', // User will fill this
      priority: 1
    });
    setDesignationOption('');
    setPhotoUrl(teacher.dp || '');
    setSelectedFile(null);
    setShowTeachersList(false);
    setSearchTerm('');
  };

  // Filter teachers based on search
  const filteredTeachers = teachers.filter(teacher => {
    if (!searchTerm) return true;
    
    return teacher.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      teacher.nameEn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      teacher.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      teacher.designation?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file.');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB.');
        return;
      }
      
      setSelectedFile(file);
      setError('');
      setShowCropper(true);
    }
  };

  const handleCropComplete = (croppedBlob) => {
    setShowCropper(false);
    
    // Create a File object from the blob
    const croppedFile = new File([croppedBlob], 'cropped-image.jpg', { type: 'image/jpeg' });
    setSelectedFile(croppedFile);
    
    // Create preview URL
    const previewUrl = URL.createObjectURL(croppedBlob);
    setPhotoUrl(previewUrl);
  };

  const uploadSelectedFile = async () => {
    if (!selectedFile) return null;
    
    setUploading(true);
    try {
      const result = await uploadMedia(selectedFile);
      if (result.success) {
        setPhotoUrl(result.url);
        return result.url;
      } else {
        setError('Upload failed: ' + (result.error || 'Unknown error'));
        return null;
      }
    } catch (error) {
      setError('Upload failed: ' + error.message);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoUrl('');
    setSelectedFile(null);
    const fileInput = document.getElementById('faculty-staff-photo-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }

    if (!formData.designation.trim()) {
      setError('Designation is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Upload photo if selected and not already uploaded
      let finalPhotoUrl = photoUrl;
      if (selectedFile && !photoUrl.startsWith('http')) {
        finalPhotoUrl = await uploadSelectedFile();
        if (finalPhotoUrl === null) {
          setLoading(false);
          return; // Upload failed, error already set
        }
      }

      const staffData = {
        name: formData.name.trim(),
        nameEn: formData.nameEn?.trim() || '',
        email: formData.email.trim(),
        phoneNumber: formData.phoneNumber?.trim() || '',
        designation: formData.designation.trim(),
        faculty: faculty._id,
        faculty_ref: faculty._id,
        departmentId: 1,
        priority: parseInt(formData.priority) || 1,
        dp: finalPhotoUrl || ''
      };

      const response = await api.post('/faculty-staffs', staffData);
      
      if (response.data.success) {
        onSave(response.data.data);
        handleClose();
      } else {
        setError(response.data.message || 'Failed to add faculty staff');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Unauthorized. Please log in again.');
      } else {
        setError(err.response?.data?.message || 'Error adding faculty staff. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        nameEn: '',
        email: '',
        phoneNumber: '',
        designation: '',
        priority: 1
      });
      setDesignationOption('');
      setPhotoUrl('');
      setSelectedFile(null);
      setError('');
      setShowTeachersList(false);
      setSearchTerm('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="dialog-overlay" onClick={handleClose}>
      <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h3>Add New Faculty Staff</h3>
          <button 
            className="close-button" 
            onClick={handleClose}
            disabled={loading}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="dialog-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {/* Teacher Selection Button */}
          <div className="form-group">
            <button
              type="button"
              onClick={() => setShowTeachersList(!showTeachersList)}
              className="select-teacher-button"
              disabled={loading || loadingTeachers}
            >
              {loadingTeachers ? 'Loading Teachers...' : showTeachersList ? 'Hide Teachers List' : '👨‍🏫 Select from Department Teachers'}
            </button>
          </div>

          {/* Teachers List */}
          {showTeachersList && (
            <div className="teachers-list-container">
              <div className="teachers-list-header">
                <h4>Select a Teacher ({filteredTeachers.length} of {teachers.length})</h4>
              </div>
              
              <div className="teachers-filter-section">
                <input
                  type="text"
                  placeholder="🔍 Search by name, email, or designation..."
                  className="teacher-search-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <div className="teachers-list">
                {filteredTeachers.length > 0 ? (
                  filteredTeachers.map((teacher) => (
                    <div
                      key={teacher._id}
                      className="teacher-item"
                      onClick={() => handleSelectTeacher(teacher)}
                    >
                      <div className="teacher-item-avatar">
                        {teacher.dp ? (
                          <img src={teacher.dp} alt={teacher.name} />
                        ) : (
                          <span className="teacher-item-initial">{teacher.name?.charAt(0) || 'T'}</span>
                        )}
                      </div>
                      <div className="teacher-item-info">
                        <h5>{teacher.name}</h5>
                        {teacher.nameEn && <p className="teacher-name-en">{teacher.nameEn}</p>}
                        <p className="teacher-designation">{teacher.designation}</p>
                        <p className="teacher-email">{teacher.email}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-teachers">
                    <p>{teachers.length === 0 ? 'No teachers found in this faculty\'s departments.' : 'No teachers match your search criteria.'}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="name">Full Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter full name"
              required
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="nameEn">English Name</label>
            <input
              type="text"
              id="nameEn"
              name="nameEn"
              value={formData.nameEn}
              onChange={handleInputChange}
              placeholder="Enter English name"
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter email address"
              required
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phoneNumber">Phone Number</label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              placeholder="Enter phone number"
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="designation">Designation *</label>
            <select
              id="designation-select"
              value={designationOption}
              onChange={(e) => {
                const val = e.target.value;
                setDesignationOption(val);
                if (val !== '__custom__') {
                  setFormData(prev => ({ ...prev, designation: val }));
                } else {
                  setFormData(prev => ({ ...prev, designation: '' }));
                }
              }}
              disabled={loading}
              className="form-input"
            >
              <option value="">পদবী নির্বাচন করুন</option>
              {DESIGNATION_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {designationOption === '__custom__' && (
              <input
                type="text"
                id="designation"
                name="designation"
                value={formData.designation}
                onChange={handleInputChange}
                placeholder="পদবী লিখুন"
                required
                disabled={loading}
                className="form-input"
                style={{ marginTop: '8px' }}
              />
            )}
          </div>

          <div className="form-group">
            <label htmlFor="priority">Priority</label>
            <input
              type="number"
              id="priority"
              name="priority"
              value={formData.priority}
              onChange={handleInputChange}
              min="1"
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="faculty-staff-photo-upload">Profile Photo</label>
            <div className="avatar-upload-container">
              <div 
                className={`circular-avatar ${selectedFile || photoUrl ? 'has-content' : ''}`}
                onClick={() => !loading && !uploading && document.getElementById('faculty-staff-photo-upload').click()}
              >
                {photoUrl ? (
                  <img src={photoUrl} alt="Profile" className="avatar-image" loading="lazy" decoding="async" />
                ) : (
                  <div className="avatar-placeholder">
                    <span className="avatar-icon">📷</span>
                    <span className="avatar-text">Click to upload</span>
                  </div>
                )}
              </div>
              
              <input
                type="file"
                id="faculty-staff-photo-upload"
                accept="image/*"
                onChange={handleFileSelect}
                disabled={loading || uploading}
                className="file-input-hidden"
                style={{ display: 'none' }}
              />
              
              {photoUrl && (
                <div className="avatar-actions">
                  <button 
                    type="button" 
                    onClick={handleRemovePhoto}
                    className="remove-button"
                    disabled={loading || uploading}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="dialog-actions">
            <button
              type="button"
              onClick={handleClose}
              className="cancel-button"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={loading || uploading}
            >
              {loading ? 'Adding...' : 'Add Staff'}
            </button>
          </div>
        </form>

        {showCropper && selectedFile && (
          <ImageCropperModal
            imageFile={selectedFile}
            onCropComplete={handleCropComplete}
            onCancel={() => {
              setShowCropper(false);
              setSelectedFile(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default AddFacultyStaffDialog;
